import { apiGet, apiPost, apiPatch } from "../api-client"

export interface TenantPlan {
  plan: {
    id: string
    name: string
    description?: string
  } | null
  start_date: string
  end_date: string | null
  user_limit: number
  discount: number
  sales_rep: string
  notes: string | null
  feature_overrides: Record<string, any>
}

export interface TenantResponse {
  tenant: {
    id: string
    name: string
    slug: string
    created_on: string
  }
  features: Record<string, any>
  plan: {
    id: number
    name: string
    slug: string | null
    start_date: string
    end_date: string | null
    discount: number
    notes: string
    sales_rep: string
    user_limit: number
    users_filled: number
  } | null
  user_count?: number
  active_users?: number
}

/**
 * Fetch current tenant information
 */
export async function getTenantInfo(): Promise<TenantResponse> {
  return apiGet<TenantResponse>("/api/tenant/")
}

// ============================================================================
// Usage Tracking Types
// ============================================================================

/**
 * Usage Record - Individual tracking event for tenant activities
 */
export interface UsageRecord {
  id: string
  tenant: string
  tenant_slug: string
  workspace?: string
  workspace_name?: string
  user: number
  user_email: string
  event_type: "plan_generated" | "extraction_completed" | "ai_chat_message" | "api_call" | "document_uploaded" | "plan_modified"
  resource_type: string
  resource_id: string
  tokens_used: number
  processing_time_ms: number
  metadata: Record<string, any>
  created_at: string
}

/**
 * Paginated response for usage records
 */
export interface UsageRecordsResponse {
  count: number
  next: string | null
  previous: string | null
  results: UsageRecord[]
}

/**
 * Query parameters for fetching usage records
 */
export interface UsageRecordsParams {
  event_type?: string
  workspace_id?: string
  user_id?: number
  start_date?: string
  end_date?: string
  page?: number
  page_size?: number
}

/**
 * Summary statistics for a usage category
 */
export interface UsageSummaryItem {
  count: number
  total_tokens: number
  total_processing_time_ms: number
  avg_processing_time_ms: number
}

export interface MonthlyBudget {
  tokens_used: number
  monthly_budget: number
  percentage: number
  remaining: number
  billing_period_start: string
}

/**
 * Summary response for usage analytics
 */
export interface UsageSummaryResponse {
  tenant: {
    id: string
    slug: string
    name: string
  }
  filters: {
    start_date: string | null
    end_date: string | null
    event_type: string | null
    workspace_id: string | null
    group_by: string
  }
  summary: Record<string, UsageSummaryItem>
  monthly_budget?: MonthlyBudget
}

/**
 * Query parameters for usage summary
 */
export interface UsageSummaryParams {
  start_date?: string
  end_date?: string
  event_type?: string
  workspace_id?: string
  group_by?: "event_type" | "resource_type" | "workspace" | "user" | "day"
}

// ============================================================================
// Usage Tracking Functions
// ============================================================================

/**
 * Fetch paginated usage records for the current tenant
 *
 * @param params - Optional query parameters for filtering and pagination
 * @returns Paginated list of usage records
 *
 * @example
 * ```typescript
 * // Fetch all records
 * const records = await fetchUsageRecords();
 *
 * // Filter by event type
 * const planRecords = await fetchUsageRecords({ event_type: "plan_generated" });
 *
 * // Filter by date range
 * const recentRecords = await fetchUsageRecords({
 *   start_date: "2025-01-01",
 *   end_date: "2025-01-31"
 * });
 * ```
 */
export async function fetchUsageRecords(params?: UsageRecordsParams): Promise<UsageRecordsResponse> {
  const queryParams = new URLSearchParams()

  if (params?.event_type) queryParams.append("event_type", params.event_type)
  if (params?.workspace_id) queryParams.append("workspace_id", params.workspace_id)
  if (params?.user_id) queryParams.append("user_id", params.user_id.toString())
  if (params?.start_date) queryParams.append("start_date", params.start_date)
  if (params?.end_date) queryParams.append("end_date", params.end_date)
  if (params?.page) queryParams.append("page", params.page.toString())
  if (params?.page_size) queryParams.append("page_size", params.page_size.toString())

  const query = queryParams.toString()
  const url = query ? `/api/tenant/usage/records/?${query}` : "/api/tenant/usage/records/"

  return apiGet<UsageRecordsResponse>(url)
}

/**
 * Fetch a single usage record by ID
 *
 * @param id - The usage record ID
 * @returns The usage record details
 *
 * @example
 * ```typescript
 * const record = await fetchUsageRecord("abc123");
 * console.log(`Event: ${record.event_type}, Tokens: ${record.tokens_used}`);
 * ```
 */
export async function fetchUsageRecord(id: string): Promise<UsageRecord> {
  return apiGet<UsageRecord>(`/api/tenant/usage/records/${id}/`)
}

/**
 * Fetch usage summary statistics for the current tenant
 *
 * @param params - Optional query parameters for filtering and grouping
 * @returns Aggregated usage statistics
 *
 * @example
 * ```typescript
 * // Summary by event type
 * const summary = await fetchUsageSummary({ group_by: "event_type" });
 *
 * // Summary for a specific workspace, grouped by day
 * const workspaceSummary = await fetchUsageSummary({
 *   workspace_id: "ws_123",
 *   group_by: "day",
 *   start_date: "2025-01-01"
 * });
 *
 * // Summary for a specific event type, grouped by user
 * const userActivity = await fetchUsageSummary({
 *   event_type: "plan_generated",
 *   group_by: "user"
 * });
 * ```
 */
export async function fetchUsageSummary(params?: UsageSummaryParams): Promise<UsageSummaryResponse> {
  const queryParams = new URLSearchParams()

  if (params?.start_date) queryParams.append("start_date", params.start_date)
  if (params?.end_date) queryParams.append("end_date", params.end_date)
  if (params?.event_type) queryParams.append("event_type", params.event_type)
  if (params?.workspace_id) queryParams.append("workspace_id", params.workspace_id)
  if (params?.group_by) queryParams.append("group_by", params.group_by)

  const query = queryParams.toString()
  const url = query ? `/api/tenant/usage/summary/?${query}` : "/api/tenant/usage/summary/"

  return apiGet<UsageSummaryResponse>(url)
}

/**
 * Fetch usage summary scoped to the current user only
 */
export async function fetchPersonalUsageSummary(): Promise<UsageSummaryResponse> {
  return apiGet<UsageSummaryResponse>("/api/tenant/usage/summary/?scope=personal&group_by=event_type")
}

// ============================================================================
// Team Member Management
// ============================================================================

export interface TeamMember {
  id: number
  email: string
  first_name: string
  last_name: string
  title: string | null
  is_active: boolean
  is_tenant_admin: boolean
}

export interface TenantUsersResponse {
  users: TeamMember[]
  seats: {
    used: number
    limit: number | null
    available: number | null
  }
}

export interface CreateUserPayload {
  email: string
  first_name?: string
  last_name?: string
  title?: string
}

export interface CreateUserResponse extends TeamMember {
  temp_password: string
}

export async function fetchTenantUsers(): Promise<TenantUsersResponse> {
  return apiGet<TenantUsersResponse>("/api/tenant/users/")
}

export async function createTenantUser(payload: CreateUserPayload): Promise<CreateUserResponse> {
  return apiPost<CreateUserResponse>("/api/tenant/users/", payload)
}

export async function deactivateTenantUser(id: number): Promise<TeamMember> {
  return apiPatch<TeamMember>(`/api/tenant/users/${id}/deactivate/`, {})
}

export async function setTenantAdmin(id: number, isAdmin: boolean): Promise<{ id: number; is_tenant_admin: boolean }> {
  return apiPatch<{ id: number; is_tenant_admin: boolean }>(`/api/tenant/users/${id}/set-admin/`, { is_tenant_admin: isAdmin })
}
